import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Link } from 'react-router-dom';
import { LogOut, TrendingUp, AlertCircle, Users, Building2, ShieldAlert, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUT_BADGE = {
  active: 'badge-success',
  trial: 'badge-info',
  past_due: 'badge-warning',
  canceled: 'badge-danger',
};
const STATUT_LABEL = {
  active: 'Active',
  trial: 'Essai',
  past_due: 'En retard',
  canceled: 'Annulée',
};

export default function SuperAdminDashboard() {
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [assos, setAssos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [urgenceAsso, setUrgenceAsso] = useState(null);
  const [urgenceRaison, setUrgenceRaison] = useState('');
  const [deletions, setDeletions] = useState([]);

  const load = async () => {
    try {
      const [d, a] = await Promise.all([api.get('/admin/dashboard'), api.get('/admin/associations')]);
      setDashboard(d.data);
      setAssos(a.data);
    } catch (e) {
      toast.error('Erreur chargement données');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get('/account/deletions-log').then(({ data }) => setDeletions(data)).catch(() => {});
  }, []);

  const declencherUrgence = async () => {
    if (!urgenceAsso || !urgenceRaison.trim()) return;
    try {
      await api.post(`/admin/urgence/${urgenceAsso.id}`, { raison: urgenceRaison });
      toast.success(`Accès d'urgence loggé pour ${urgenceAsso.nom}. Le président a été notifié.`);
      setUrgenceAsso(null);
      setUrgenceRaison('');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-50/40" data-testid="superadmin-dashboard">
      {/* Topbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg grid place-items-center text-white font-black" style={{ background: '#0052FF' }}>A</div>
            <div>
              <div className="font-display text-base font-black tracking-tight text-slate-900 leading-none">AssoPerf</div>
              <div className="text-[10px] text-slate-500 font-bold tracking-wider uppercase mt-0.5">Super Admin</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-sm font-semibold text-slate-900">{user?.prenom} {user?.nom}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
            <button onClick={logout} className="btn-ghost flex items-center gap-1.5" data-testid="superadmin-logout">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 lg:py-12">
        <div className="mb-10 slide-up">
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mb-2">Tableau de bord</h1>
          <p className="text-slate-600">Vue d'ensemble SaaS · Aucune donnée privée des associations n'est visible ici.</p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10 stagger" data-testid="superadmin-kpis">
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <div className="label-uppercase">MRR</div>
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="font-display text-3xl lg:text-4xl font-black tracking-tighter text-slate-900" data-testid="kpi-mrr">{Number(dashboard?.mrr || 0).toFixed(0)}€</div>
            <div className="text-xs text-slate-500 mt-1">par mois</div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <div className="label-uppercase">ARR</div>
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="font-display text-3xl lg:text-4xl font-black tracking-tighter text-slate-900" data-testid="kpi-arr">{Number(dashboard?.arr || 0).toFixed(0)}€</div>
            <div className="text-xs text-slate-500 mt-1">annuel</div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <div className="label-uppercase">Assos actives</div>
              <Building2 className="w-4 h-4 text-slate-400" />
            </div>
            <div className="font-display text-3xl lg:text-4xl font-black tracking-tighter text-slate-900" data-testid="kpi-assos-actives">{dashboard?.assos?.actives || 0}</div>
            <div className="text-xs text-slate-500 mt-1">/ {dashboard?.assos?.total || 0} total</div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <div className="label-uppercase">Alertes</div>
              <AlertCircle className="w-4 h-4 text-amber-500" />
            </div>
            <div className="font-display text-3xl lg:text-4xl font-black tracking-tighter text-slate-900" data-testid="kpi-alertes">{dashboard?.alertes?.length || 0}</div>
            <div className="text-xs text-slate-500 mt-1">à traiter</div>
          </div>
        </div>

        {/* Répartition plans */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
          {(dashboard?.repartition_plans || []).map((p) => (
            <div key={p.plan_id} className="card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-uppercase">{p.plan_id}</div>
                  <div className="font-display text-3xl font-black text-slate-900 mt-2">{p.nb}</div>
                  <div className="text-xs text-slate-500 mt-1">{Number(p.mrr).toFixed(0)}€ MRR</div>
                </div>
                <div className="w-14 h-14 rounded-xl bg-slate-100 grid place-items-center text-slate-700">
                  <Users className="w-6 h-6" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Alertes */}
        {(dashboard?.alertes?.length > 0) && (
          <div className="card mb-10 border-amber-200 bg-amber-50/30">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <h2 className="font-display text-lg font-bold text-slate-900">Alertes paiements</h2>
            </div>
            <div className="space-y-2" data-testid="superadmin-alertes">
              {dashboard.alertes.map((a) => (
                <div key={a.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                  <div>
                    <div className="font-semibold text-slate-900">{a.nom}</div>
                    <div className="text-xs text-slate-500">Plan {a.plan_id}</div>
                  </div>
                  <span className={`badge ${STATUT_BADGE[a.abonnement_statut] || 'badge-neutral'}`}>{STATUT_LABEL[a.abonnement_statut]}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Liste assos */}
        <div className="card overflow-hidden !p-0">
          <div className="p-6 border-b border-slate-200">
            <h2 className="font-display text-lg font-bold text-slate-900">Toutes les associations</h2>
            <p className="text-sm text-slate-500 mt-1">Vue limitée aux données de facturation. Aucun accès aux membres/événements internes.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="superadmin-assos-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/50">
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Association</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Type</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Plan</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Statut</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Users</th>
                  <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assos.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`asso-row-${a.id}`}>
                    <td className="p-4">
                      <div className="font-semibold text-slate-900">{a.nom}</div>
                      <div className="text-xs text-slate-500">{a.ville || '—'}</div>
                    </td>
                    <td className="p-4 text-sm text-slate-600 capitalize">{a.type}</td>
                    <td className="p-4">
                      <span className="badge badge-neutral capitalize">{a.plan_id} · {Number(a.prix_mensuel).toFixed(0)}€</span>
                    </td>
                    <td className="p-4">
                      <span className={`badge ${STATUT_BADGE[a.abonnement_statut] || 'badge-neutral'}`}>{STATUT_LABEL[a.abonnement_statut]}</span>
                    </td>
                    <td className="p-4 text-sm text-slate-700">{a.nb_users}</td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => setUrgenceAsso(a)}
                        className="btn-ghost text-xs flex items-center gap-1 ml-auto !text-amber-700 hover:!bg-amber-50"
                        data-testid={`urgence-btn-${a.id}`}
                      >
                        <ShieldAlert className="w-3.5 h-3.5" /> Accès urgence
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Journal RGPD des suppressions de compte */}
        <div className="card overflow-hidden !p-0 mt-8" data-testid="deletions-log-card">
          <div className="p-6 border-b border-slate-200 flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-slate-400" />
            <div>
              <h2 className="font-display text-lg font-bold text-slate-900">Comptes supprimés (RGPD)</h2>
              <p className="text-sm text-slate-500 mt-1">Journal anonymisé : date, association et rôle. Aucune donnée personnelle conservée.</p>
            </div>
          </div>
          {deletions.length === 0 ? (
            <div className="p-6 text-sm text-slate-500" data-testid="deletions-log-empty">Aucune suppression de compte enregistrée.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="deletions-log-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/50">
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Date</th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Association</th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Rôle</th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Détail</th>
                  </tr>
                </thead>
                <tbody>
                  {deletions.map((d) => (
                    <tr key={d.id} className="border-b border-slate-100">
                      <td className="p-4 text-sm text-slate-700">
                        {d.created_at ? new Date(d.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                      </td>
                      <td className="p-4 text-sm font-semibold text-slate-900">{d.asso_nom || '—'}</td>
                      <td className="p-4 text-sm text-slate-600 capitalize">{(d.role || '').replace('_', ' ') || '—'}</td>
                      <td className="p-4 text-xs text-slate-500">
                        {d.asso_archivee ? 'Association archivée' : d.transfert_vers ? 'Présidence transférée' : 'Compte anonymisé'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Modal urgence */}
      {urgenceAsso && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 fade-in" onClick={() => setUrgenceAsso(null)}>
          <div className="card max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="urgence-modal">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              <h3 className="font-display text-lg font-bold text-slate-900">Accès d'urgence</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Vous demandez un accès urgence pour <strong>{urgenceAsso.nom}</strong>.
              Cet accès est limité à 2h, intégralement loggé, et le président sera notifié par email.
            </p>
            <label className="label-uppercase block mb-2">Raison (obligatoire)</label>
            <textarea
              className="input-base mb-4"
              rows={3}
              value={urgenceRaison}
              onChange={(e) => setUrgenceRaison(e.target.value)}
              placeholder="Ex: Demande explicite du président pour résoudre un blocage paiement"
              data-testid="urgence-raison-input"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setUrgenceAsso(null)} className="btn-secondary" data-testid="urgence-cancel">Annuler</button>
              <button onClick={declencherUrgence} disabled={!urgenceRaison.trim()} className="btn-primary" data-testid="urgence-confirm">Confirmer l'accès</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
